import { selectKaraokeVariantForLanguage } from '@src/editor/models/karaoke/selectKaraokeVariant';
import { normalizePlacementMeta } from '@src/repositories/normalizePlacementMeta';
import { LogManager } from '@src/utility/Logger';
import { Uploader } from '@src/utility/uploader';
import MD5 from 'crypto-js/md5';
import { z } from 'zod';

import { extractErrorInfo } from '../utility/error';

import type { KaraokeData } from '@src/editor/types/karaoke';
import type { CachedVideoRepository } from '@src/repositories/CachedVideoRepository';
import type { VideoRepository } from '@src/repositories/VideoRepository';
import type { Video } from '@src/types.ts/story';
import type {
  KaraokeLanguageMetadata,
  VideoResource,
  VideoResourceMetadata,
  VideoResources,
} from '@src/types.ts/video';

export const VideoSourceSchema = z
  .object({
    width: z.number().optional(),
    height: z.number().optional(),
    frame_rate: z.union([z.number(), z.string()]).optional(),
    duration: z.number().optional(),
    elements: z.array(z.any()).optional(),
  })
  .passthrough();

export const VideoTranscriptSnapshotSchema = z
  .object({
    elements: z.array(z.any()),
  })
  .passthrough();

export const VideoKaraokeDataSchema = z
  .object({
    clips: z.array(
      z
        .object({
          id: z.string(),
          transcriptTokenIds: z.array(z.string()),
          hiddenTranscriptTokenIds: z.array(z.string()).optional(),
          ignoredByFiltersIds: z.array(z.string()).optional(),
          startSecondsOffset: z.number().optional(),
          endSecondsOffset: z.number().optional(),
          karaokeConfigId: z.string().optional(),
          translatedText: z.string().optional(),
          // `originalIndices` (PLAT-9623) is the phrase's source identity and is
          // load-bearing, not decoration: `attachLeadIdentityFromTokens` needs
          // it to recover each phrase's `leadUuid`, which becomes the caption
          // word span's `data-source-uuid`
          translatedPhrases: z
            .array(
              z
                .object({
                  text: z.string(),
                  ts: z.number(),
                  endTs: z.number(),
                  originalIndices: z.array(z.number()).optional(),
                })
                .passthrough(),
            )
            .optional(),
        })
        .passthrough(),
    ),
    configs: z.array(z.tuple([z.string(), z.any()])),
    defaultConfigId: z.string().nullable(),
  })
  .passthrough();

// PLAT-10788 — a single resolved/stored resource pointer (signed `url`, or a
// `fileKey` to exchange for one). Shared by the karaoke union below.
export const VideoResourcePointerSchema = z
  .object({
    url: z.string().url().optional(),
    fileKey: z.string().min(1).optional(),
    fileName: z.string().optional(),
    md5Hash: z.string().optional(),
  })
  .passthrough();

export const VideoResourcesSchema = z
  .object({
    videoSourceSnapshot: z
      .object({
        // Either a directly consumable (signed) CloudFront URL OR a fileKey that we need to exchange for a signed URL.
        url: z.string().url().optional(),
        fileKey: z.string().min(1).optional(),
        fileName: z.string().optional(),
        md5Hash: z.string().optional(),
      })
      .passthrough()
      .optional(),
    transcriptionSnapshot: z
      .object({
        url: z.string().url().optional(),
        fileKey: z.string().min(1).optional(),
        fileName: z.string().optional(),
        md5Hash: z.string().optional(),
      })
      .passthrough()
      .optional(),
    karaokeData: z
      // PLAT-10788 — legacy flat pointer OR a per-language map. `z.record` first
      // so a flat pointer (string-valued keys) fails the record branch and falls
      // through to the single-pointer branch.
      .union([z.record(VideoResourcePointerSchema), VideoResourcePointerSchema])
      .optional(),
    scenes: z
      .object({
        url: z.string().url().optional(),
        fileKey: z.string().min(1).optional(),
        fileName: z.string().optional(),
        md5Hash: z.string().optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();

const logger = LogManager.getLogger('service:VideoResourcesProvider');

/**
 * Fetches a video source from a specific URL
 *
 * @param videoSourceUrl URL to fetch video source from
 * @returns VideoSource json
 */
async function fetchJsonResourceFromUrl<Z extends z.ZodSchema>(
  resourceUrl: string,
  resourceSchema: Z,
): Promise<z.infer<Z>> {
  const jsonResponse = await fetch(resourceUrl);
  const json: unknown = await jsonResponse.json();
  const parsed = resourceSchema.safeParse(json);
  if (!parsed.success) {
    logger.warn('Resource schema validation failed', {
      url: resourceUrl,
      issues: parsed.error.issues.slice(0, 5),
    });
    // Return raw payload with assertion to avoid blocking caller; future: tighten schema & migrate.
    // eslint-disable-next-line no-restricted-syntax
    return json as z.infer<Z>;
  }
  return parsed.data;
}

// ---------------------------------------------------------------------------
// S3 Signed URL Resolution
// ---------------------------------------------------------------------------
// During migration we may receive resource metadata lacking a pre-signed URL
// (only a fileKey). In that case we request a one-off signed URL from the
// upload service. This preserves backwards compatibility with the legacy
// Cloud Function that returned direct CloudFront links.

async function fetchSignedUrlForFileKey(
  fileKey: string,
): Promise<string | null> {
  try {
    const signUrl = `${
      process.env.REACT_APP_API_URL
    }/api/upload/signed-url?fileKey=${encodeURIComponent(fileKey)}`;
    const resp = await fetch(signUrl, { method: 'GET' });
    if (!resp.ok) {
      logger.warn('Failed to get signed URL for fileKey', {
        fileKey,
        status: resp.status,
      });
      return null;
    }
    const json = await resp.json();
    const data: unknown = json?.data;
    if (data && typeof data === 'object' && 'url' in data) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const potentialUrl = (data as { [k: string]: unknown })['url'];
      if (typeof potentialUrl === 'string') return potentialUrl;
    }
    logger.warn('Signed URL response missing url field', { fileKey });
    return null;
  } catch (error) {
    logger.error('Exception while requesting signed URL', { fileKey, error });
    return null;
  }
}

async function resolveSnapshotUrl(rec: {
  url?: string;
  fileKey?: string;
}): Promise<string | null> {
  const maybeUrl = rec.url;
  if (typeof maybeUrl === 'string' && maybeUrl.length > 0) return maybeUrl;
  const maybeFileKey = rec.fileKey;
  if (typeof maybeFileKey === 'string' && maybeFileKey.length > 0) {
    return fetchSignedUrlForFileKey(maybeFileKey);
  }
  return null;
}

/**
 * PLAT-10788 — is a karaoke resource value a per-language map (`{ [lang]: pointer }`)
 * vs a legacy flat pointer? A flat pointer carries pointer keys at top level; a
 * map's own values are the pointer objects. Defensive (backend does no validation).
 */
export function isKaraokeLanguageMap(
  value: unknown,
): value is Record<string, { url?: string; fileKey?: string }> {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if ('url' in v || 'fileKey' in v || 'fileName' in v || 'md5Hash' in v) {
    return false;
  }
  const entries = Object.values(v);
  return (
    entries.length > 0 &&
    entries.every(
      e =>
        !!e &&
        typeof e === 'object' &&
        ('url' in (e as object) ||
          'fileKey' in (e as object) ||
          'fileName' in (e as object)),
    )
  );
}

/**
 * PLAT-10788 — resolve the karaoke pointer to signed URLs: a per-language map
 * → `{ [lang]: { url } }`, a legacy flat pointer → `{ url }`, else undefined.
 */
async function resolveKaraokeResource(
  karaokeData: unknown,
): Promise<VideoResource | Record<string, VideoResource> | undefined> {
  if (!karaokeData) return undefined;
  if (isKaraokeLanguageMap(karaokeData)) {
    const perLanguage: Record<string, VideoResource> = {};
    await Promise.all(
      Object.entries(karaokeData).map(async ([lang, rec]) => {
        const url = await resolveSnapshotUrl(rec);
        if (url) perLanguage[lang] = { url };
      }),
    );
    return Object.keys(perLanguage).length > 0 ? perLanguage : undefined;
  }
  const url = await resolveSnapshotUrl(
    karaokeData as { url?: string; fileKey?: string },
  );
  return url ? { url } : undefined;
}

/**
 * PLAT-10788 — fetch the karaoke blob(s) and rebuild each `configs` array into a
 * `Map`. A per-language resource yields `{ byLanguage }`; a legacy flat pointer
 * yields `{ flat }`. The store layer then keys/activates per DS8/DS9.
 */
async function fetchKaraokeResource(
  karaokeResource: unknown,
): Promise<
  | { byLanguage: Record<string, KaraokeData> }
  | { flat: KaraokeData }
  | undefined
> {
  const rebuild = (
    serialized: z.infer<typeof VideoKaraokeDataSchema>,
  ): KaraokeData =>
    ({ ...serialized, configs: new Map(serialized.configs) }) as KaraokeData;

  if (!karaokeResource) return undefined;

  if (isKaraokeLanguageMap(karaokeResource)) {
    const byLanguage: Record<string, KaraokeData> = {};
    await Promise.all(
      Object.entries(karaokeResource).map(async ([lang, rec]) => {
        if (!rec.url) return;
        const serialized = await fetchJsonResourceFromUrl(
          rec.url,
          VideoKaraokeDataSchema,
        );
        if (serialized) byLanguage[lang] = rebuild(serialized);
      }),
    );
    return Object.keys(byLanguage).length > 0 ? { byLanguage } : undefined;
  }

  const url = (karaokeResource as VideoResource).url;
  if (!url) return undefined;
  const serialized = await fetchJsonResourceFromUrl(
    url,
    VideoKaraokeDataSchema,
  );
  return serialized ? { flat: rebuild(serialized) } : undefined;
}

export async function requestVideoResources(
  storyId: string,
  videoId: string,
  videoVersionId: string | 'latest',
  fields: VideoResourceField[],
): Promise<VideoResources> {
  const query = new URLSearchParams({
    storyId,
    videoId,
    versionId: videoVersionId,
    fields: fields.join(','),
  }).toString();
  const url = `${process.env.REACT_APP_API_URL}/api/video/version-resources?${query}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (response.ok) {
    const responseJson = await response.json();
    if (!responseJson.data) {
      throw new Error('Response data is empty');
    }

    const parsed = VideoResourcesSchema.safeParse(responseJson.data);
    if (!parsed.success) {
      logger.warn('VideoResources validation failed', {
        issues: parsed.error.issues.slice(0, 5),
      });
      // Fallback: return raw data cast (best-effort). This preserves backward compatibility.
      // eslint-disable-next-line no-restricted-syntax
      return responseJson.data as VideoResources;
    }
    const shaped: VideoResources = {};

    // Resolve potentially absent signed URLs (fileKey only cases) in parallel.
    // Karaoke is resolved map-aware (per-language or legacy flat) — PLAT-10788.
    const [videoSourceUrl, transcriptionUrl, scenesUrl, karaokeResolved] =
      await Promise.all([
        parsed.data.videoSourceSnapshot
          ? resolveSnapshotUrl(parsed.data.videoSourceSnapshot)
          : Promise.resolve(undefined),
        parsed.data.transcriptionSnapshot
          ? resolveSnapshotUrl(parsed.data.transcriptionSnapshot)
          : Promise.resolve(undefined),
        parsed.data.scenes
          ? resolveSnapshotUrl(parsed.data.scenes)
          : Promise.resolve(undefined),
        resolveKaraokeResource(parsed.data.karaokeData),
      ]);

    if (videoSourceUrl) {
      shaped.videoSourceSnapshot = { url: videoSourceUrl };
    }
    if (transcriptionUrl) {
      shaped.transcriptionSnapshot = { url: transcriptionUrl };
    }
    if (karaokeResolved) {
      shaped.karaokeData = karaokeResolved;
    }
    if (scenesUrl) {
      shaped.scenes = { url: scenesUrl };
    }
    return shaped;
  } else {
    console.error('Failed to fetch cloudfront links');
    return {};
  }
}

/** A resolved render artifact from `/api/publish-record/resources` — the fileKey
 *  pointer plus a server-signed CloudFront `url`. */
export type PublishRecordResource = { url: string } & Record<string, unknown>;

export type PublishRecordOutputField = 'video' | 'scenes' | 'thumbnail';

/**
 * Resolve CloudFront URLs for a PublishRecord's rendered artifacts from their
 * `output_json` fileKey pointers. Mimics {@link requestVideoResources}: the
 * client holds only fileKeys; the backend signs them. Used to synthesize a
 * playable/downloadable `videoFilePrimary` from a record's S3 output.
 *
 * Never throws: an unreachable or erroring endpoint returns `{}`, the same as a
 * record with no artifact. Callers flatten both to "no URL" anyway, and leaving
 * only the network failure to reject made a single blip abort a whole batch.
 */
export async function requestPublishRecordResources(
  recordId: string,
  fields?: PublishRecordOutputField[],
): Promise<Partial<Record<PublishRecordOutputField, PublishRecordResource>>> {
  const params = new URLSearchParams({ recordId });
  if (fields?.length) params.set('fields', fields.join(','));
  const url = `${
    process.env.REACT_APP_API_URL
  }/api/publish-record/resources?${params.toString()}`;
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (response.ok) {
      const json = await response.json();
      return json?.data ?? {};
    }
    logger.error('Failed to fetch publish record resources', {
      recordId,
      status: response.status,
    });
  } catch (error) {
    logger.error('Failed to fetch publish record resources', {
      recordId,
      error: extractErrorInfo(error),
    });
  }
  return {};
}

type PopulatableVideo = Pick<Video, 'id'> & {
  videoSource?: Video['videoSource'];
  videoJson?: Video['videoSource'];
  versionId?: string;
  transcriptionSnapshot?: Video['transcriptionSnapshot'];
  transcriptionChanges?: Video['transcriptionChanges'];
  karaokeData?: Video['karaokeData'];
  karaokeDataByLanguage?: Video['karaokeDataByLanguage'];
  scenes?: Video['scenes'];
};

type PopulatedVideo<
  V extends PopulatableVideo,
  R extends PopulatableProperties[],
> = V & {
  [K in R[number]]: NonNullable<V[K]>;
};

export type VideoResourceField =
  | 'videoSourceSnapshot'
  | 'transcriptionSnapshot'
  | 'karaokeData'
  | 'scenes';
type PopulatableProperties =
  | 'videoSource'
  | 'transcriptionSnapshot'
  | 'karaokeData'
  | 'scenes';

/** A mapped video version can carry a metadata-only source shell (dimensions,
 * duration, fonts) before its S3 snapshot is hydrated. It is not a usable
 * editor source until the `elements` field itself has been populated. */
function hasPopulatedVideoSource(
  videoSource: PopulatableVideo['videoSource'] | undefined,
): boolean {
  return Array.isArray(videoSource?.elements);
}

function ensureVideoHasResources<
  V extends PopulatableVideo,
  R extends PopulatableProperties[],
>(video: V, populatedProperties: R): asserts video is PopulatedVideo<V, R> {
  if (populatedProperties.includes('videoSource') && !video.videoSource) {
    throw new Error('Invariant: videoSource expected to be populated');
  }
  if (
    populatedProperties.includes('transcriptionSnapshot') &&
    !video.transcriptionSnapshot
  ) {
    throw new Error(
      'Invariant: transcriptionSnapshot expected to be populated',
    );
  }
  if (populatedProperties.includes('karaokeData') && !video.karaokeData) {
    throw new Error('Invariant: karaokeData expected to be populated');
  }
  if (populatedProperties.includes('scenes') && !video.scenes) {
    throw new Error('Invariant: scenes expected to be populated');
  }
}

export async function populateVideoVersionWithResources<
  V extends PopulatableVideo,
>(
  storyId: string,
  video: V,
  resourceFields: VideoResourceField[],
  skipIfAlreadyPopulated: boolean = true,
  versionId: string | 'latest' = video.versionId || 'latest',
  throwIfMissing: boolean = true,
): Promise<PopulatedVideo<V, PopulatableProperties[]>> {
  let populatedProperties: PopulatableProperties[] = [];
  if (resourceFields.includes('videoSourceSnapshot')) {
    if (hasPopulatedVideoSource(video.videoSource) && skipIfAlreadyPopulated) {
      logger.debug(
        'VideoSourceProvider | video source already populated, skipping fetch',
      );
      populatedProperties.push('videoSource');
    }
    if (
      !populatedProperties.includes('videoSource') &&
      hasPopulatedVideoSource(video.videoJson) &&
      skipIfAlreadyPopulated
    ) {
      logger.debug('VideoSourceProvider | taking from videoJson');
      video.videoSource = video.videoJson;
      populatedProperties.push('videoSource');
    }
    if (!populatedProperties.includes('videoSource') && !video.id) {
      throw new Error(
        'populateVideoVersionWithResources: video.id is required to fetch video source',
      );
    }
  }

  if (resourceFields.includes('transcriptionSnapshot')) {
    // DO NOT SKIP FETCH
    // if (video.transcriptionSnapshot && skipIfAlreadyPopulated) {
    //   logger.debug(
    //     'VideoSourceProvider | transcription snapshot already populated, skipping fetch',
    //   );
    //   populatedProperties.push('transcriptionSnapshot');
    // }
    if (!populatedProperties.includes('transcriptionSnapshot') && !video.id) {
      throw new Error(
        'populateVideoVersionWithResources: video.id is required to fetch transcription',
      );
    }
  }

  if (resourceFields.includes('karaokeData')) {
    if (video.karaokeData && skipIfAlreadyPopulated) {
      logger.debug(
        'VideoSourceProvider | karaokeData already populated, skipping fetch',
      );
      populatedProperties.push('karaokeData');
    }
    if (!populatedProperties.includes('karaokeData') && !video.id) {
      throw new Error(
        'populateVideoVersionWithResources: video.id is required to fetch karaokeData',
      );
    }
  }

  if (resourceFields.includes('scenes')) {
    if (video.scenes && skipIfAlreadyPopulated) {
      logger.debug(
        'VideoSourceProvider | scenes already populated, skipping fetch',
      );
      populatedProperties.push('scenes');
    }
    if (!populatedProperties.includes('scenes') && !video.id) {
      throw new Error(
        'populateVideoVersionWithResources: video.id is required to fetch scenes',
      );
    }
  }

  if (populatedProperties.includes('videoSource')) {
    resourceFields = resourceFields.filter(f => f !== 'videoSourceSnapshot');
  }

  if (populatedProperties.includes('transcriptionSnapshot')) {
    resourceFields = resourceFields.filter(f => f !== 'transcriptionSnapshot');
  }

  if (populatedProperties.includes('karaokeData')) {
    resourceFields = resourceFields.filter(f => f !== 'karaokeData');
  }

  if (populatedProperties.includes('scenes')) {
    resourceFields = resourceFields.filter(f => f !== 'scenes');
  }

  if (resourceFields.length > 0 && video.id) {
    const resources = await requestVideoResources(
      storyId,
      video.id,
      versionId,
      resourceFields,
    );

    // PLAT-10788 — a resource is "present" when it resolved a signed `url`.
    // Karaoke may be a per-language map (`{ [lang]: { url } }`) with no top-level
    // `url`; it counts as present when any language resolved a url.
    const missingFields = resourceFields.filter(field => {
      const rec = resources[field];
      if (field === 'karaokeData' && isKaraokeLanguageMap(rec)) {
        for (const lang in rec) {
          if (!rec[lang]?.url) return true;
        }
        return false;
      }
      return !rec?.url;
    });
    if (missingFields.length > 0) {
      if (throwIfMissing) {
        throw new Error(
          `Failed to fetch requested video resources: ${missingFields.join(
            ', ',
          )}`,
        );
      } else {
        logger.warn('Some video resources could not be fetched', {
          missingFields,
          videoId: video.id,
        });
        // Filter out missing fields so we don't try to fetch them
        resourceFields = resourceFields.filter(f => !missingFields.includes(f));
      }
    }
    const [transcriptionSnapshot, videoSource, karaokeFetched, scenes] =
      await Promise.all([
        resources.transcriptionSnapshot?.url
          ? fetchJsonResourceFromUrl(
              resources.transcriptionSnapshot.url,
              VideoTranscriptSnapshotSchema,
            )
          : Promise.resolve(undefined),
        resources.videoSourceSnapshot?.url
          ? fetchJsonResourceFromUrl(
              resources.videoSourceSnapshot.url,
              VideoSourceSchema,
            )
          : Promise.resolve(undefined),
        fetchKaraokeResource(resources.karaokeData),
        resources.scenes?.url
          ? fetchJsonResourceFromUrl(resources.scenes.url, z.array(z.any()))
          : Promise.resolve(undefined),
      ]);

    if (transcriptionSnapshot) {
      video.transcriptionSnapshot = transcriptionSnapshot;
      populatedProperties.push('transcriptionSnapshot');
    }
    if (videoSource) {
      video.videoSource = videoSource;
      populatedProperties.push('videoSource');
    }
    // PLAT-10788 — per-language map → `karaokeDataByLanguage` (+ a bootstrap
    // `karaokeData` for legacy consumers; the store refines the active entry per
    // DS8). Legacy flat blob → single `karaokeData` (the store wraps it into the
    // map per DS9). See Phase 4c.
    if (karaokeFetched && 'byLanguage' in karaokeFetched) {
      video.karaokeDataByLanguage = karaokeFetched.byLanguage;
      // Pick the source variant as the bootstrap default for legacy consumers;
      // the store refines to the true active entry per DS8.
      video.karaokeData =
        selectKaraokeVariantForLanguage(karaokeFetched.byLanguage, {
          isOriginal: true,
          langCode: 'en',
        }) ?? Object.values(karaokeFetched.byLanguage)[0];
      populatedProperties.push('karaokeData');
    } else if (karaokeFetched) {
      video.karaokeData = karaokeFetched.flat;
      populatedProperties.push('karaokeData');
    }
    if (scenes) {
      video.scenes = scenes;
      populatedProperties.push('scenes');
    }

    logger.debug('Populated video resources', {
      resourceFields,
      videoId: video.id,
    });
  }

  if (throwIfMissing) {
    ensureVideoHasResources(video, populatedProperties);
  }
  // When throwIfMissing is false, we can't guarantee all requested properties are populated,
  // but caller accepts this by setting throwIfMissing=false.
  // eslint-disable-next-line no-restricted-syntax
  return video as PopulatedVideo<V, PopulatableProperties[]>;
}

/**
 * Fetches extraElementData for a video using VideoRepository.
 *
 * @param videoRepository - The VideoRepository instance to use for fetching
 * @param videoId - The ID of the video to fetch extraElementData for
 * @returns The parsed extraElementData (empty if not found/failed), and the
 * video's current Dato version and resource pointers from the same response
 * (PLAT-11911)
 */
export async function populateExtraElementData(
  videoRepository: VideoRepository | CachedVideoRepository,
  videoId: string,
): Promise<{
  extraElementData: Video['extraElementData'];
  versionId?: string;
  resourcesMetadata?: Video['resourcesMetadata'];
}> {
  try {
    const videoItem = await videoRepository.findVideo(videoId);
    if (!videoItem) {
      logger.warn('populateExtraElementData: Video not found', { videoId });
      return { extraElementData: {} };
    }

    // PLAT-10921 E11 — the SECOND boundary that turns `extra_elements_json`
    // into a `Video['extraElementData']` (the other is
    // `VideoRepository.mapVideoVersions`). Both must normalize, or a story
    // loaded through this path would reach the placement reconciler still
    // speaking the legacy `manuallyPositioned` vocabulary — and unnormalized
    // legacy graphics are exactly what must never move.
    const extraElementData: Video['extraElementData'] = normalizePlacementMeta(
      videoItem.extra_elements_json
        ? JSON.parse(videoItem.extra_elements_json)
        : {},
    );

    logger.debug(
      'populateExtraElementData: Successfully fetched extraElementData',
      {
        videoId,
        keyCount: Object.keys(extraElementData).length,
      },
    );

    return {
      extraElementData,
      versionId: videoItem.meta?.current_version,
      // Parsed as `VideoRepository.mapVideoVersions` does for a cold load.
      resourcesMetadata: JSON.parse(videoItem.resources_metadata_json ?? '{}'),
    };
  } catch (error) {
    logger.error('populateExtraElementData: Failed to fetch extraElementData', {
      videoId,
      error,
    });
    return { extraElementData: {} };
  }
}

export type SavedVideoResourcesMetadata = Partial<
  Record<
    Exclude<VideoResourceField, 'karaokeData'>,
    VideoResourceMetadata | null
  >
> & {
  // PLAT-10788 — karaoke is uploaded per language: one S3 blob + pointer per
  // BCP-47 code. Languages whose upload failed (null metadata) are dropped.
  karaokeData?: KaraokeLanguageMetadata;
};

export async function saveVideoResources(
  storyId: string,
  resources: Partial<Record<VideoResourceField, object>>,
): Promise<SavedVideoResourcesMetadata> {
  for (const key of Object.keys(resources) as VideoResourceField[]) {
    if (!resources[key]) {
      throw new Error(`Resource for field ${key} is not set`);
    }
  }

  const result: SavedVideoResourcesMetadata = {};

  await Promise.all(
    (Object.entries(resources) as [VideoResourceField, object][]).map(
      async ([field, data]) => {
        if (field === 'karaokeData') {
          // PLAT-10788 — `karaokeData` is a per-language map
          // (`{ [lang]: KaraokeData }`); upload one language-suffixed blob each.
          const byLanguage = data as Record<string, object>;
          const perLanguage: KaraokeLanguageMetadata = {};
          await Promise.all(
            Object.entries(byLanguage).map(async ([lang, kd]) => {
              const meta = await saveVideoResource(
                storyId,
                kd,
                'karaokeData',
                lang,
              );
              if (meta) perLanguage[lang] = meta;
            }),
          );
          // Only record karaoke when at least one language uploaded. An empty
          // `{}` is truthy, so it would look "present" to callers' truthiness
          // checks (masking the "Did not save karaoke" warning and persisting a
          // useless empty map). Leave the key ABSENT — not `undefined`, which
          // callers' `.some(k => !meta[k])` validation would flag as a failure.
          if (Object.keys(perLanguage).length > 0) {
            result.karaokeData = perLanguage;
          }
        } else {
          result[field] = await saveVideoResource(storyId, data, field);
        }
      },
    ),
  );

  return result;
}

/**
 * JSON.stringify replacer that converts Map and Set instances to
 * JSON-safe structures.  Maps become `[key, value]` tuple arrays
 * (matching the format expected by `new Map(...)` on the load path),
 * and Sets become plain arrays.
 *
 * Without this, `JSON.stringify` silently serialises both as `{}`.
 */
function jsonSafeReplacer(_key: string, value: unknown): unknown {
  if (value instanceof Map) {
    return Array.from(value.entries());
  }
  if (value instanceof Set) {
    return Array.from(value);
  }
  return value;
}

export async function saveVideoResource(
  storyId: string,
  resource: object,
  resourceField: VideoResourceField,
  // PLAT-10788 — optional per-language suffix for the file name (karaoke stores
  // one blob per language: `karaokeData-<lang>-<ts>.json`).
  variant?: string,
): Promise<VideoResourceMetadata | null> {
  // ------------------------------------------------------------------
  // New implementation: persist JSON snapshot to S3 directly using the
  // existing multipart upload endpoints (/api/upload/*) consumed by
  // Uploader (initialize -> pre-sign-urls -> PUT parts -> finalize).
  // We locally compute the md5 hash so metadata matches former
  // Cloud Function response shape { fileKey, fileName, md5Hash }.
  // Assumption: backend requestVideoResources builds CloudFront URLs
  // from stored fileKey; thus we pick a deterministic key prefix.
  // ------------------------------------------------------------------

  try {
    const jsonString = JSON.stringify(resource, jsonSafeReplacer, 2);
    const md5Hash = MD5(jsonString).toString();

    // Use a stable folder prefix so backend can serve it later.
    // Pattern: video-resources/<storyId>/<resourceField>/<uuid>.json
    const fileName = `${resourceField}${variant ? `-${variant}` : ''}-${new Date()
      .toISOString()
      .replace(/[:.]/g, '-')}.json`;
    const fileKey = `sessions/${storyId}/resources/${fileName}`;

    const file = new File([jsonString], fileName, {
      type: 'application/json',
      lastModified: Date.now(),
    });

    await new Promise<void>((resolve, reject) => {
      const uploader = new Uploader({
        file,
        fileName,
        fileKey,
        baseURL: `${process.env.REACT_APP_API_URL}/api/upload`,
        chunkSize: 10, // MB (min 5MB enforced by uploader)
        threadsQuantity: 1,
        useTransferAcceleration: true,
        onCompleteFn: () => resolve(),
        onError: error => reject(error),
      });
      uploader.start();
    });

    const metadata: VideoResourceMetadata = {
      fileKey,
      fileName,
      md5Hash,
    };

    logger.debug(`Saved ${resourceField} to S3 via multipart upload`, {
      metadata,
      storyId,
    });

    return metadata;
  } catch (error) {
    logger.error('Failed to save video resource to S3', {
      storyId,
      resourceField,
      error: extractErrorInfo(error),
    });
    return null;
  }
}
